/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useRef } from 'react'
import { uploaderService } from '../../../services/uploader'
import { Formik, Form } from 'formik'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../../root'
import ModalDialogFn from '../../../components/Modal'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import { useModal } from '../../../contexts/ModalContext'
import { ErrorMessageStyled } from '../../../components/shared/ErrorMessage'
import FileUploadModal from '../../../components/FileUploadModal'
import type { Task } from '../../../services/api/types'
import { useEsignature } from '~/hooks/useEsignature'
import RcmEsignatureModal from '~/components/esignature/RcmEsignatureModal'

// Import constants - these may need to be created or imported from legacy
const FsiType = { PLACEHOLDER: 'PLACEHOLDER' } as const
const FolderName = { CLINICAL: 'Clinical', MEDIDATA: 'Medidata' } as const

interface UploadFileRequestModalProps {
  parentId: number
  params: {
    task: Task
    workflowId: number
    fileToUpload?: File
    uploadToVersion?: boolean
    isFileExists?: boolean
  }
  onSuccess: (uploadedFileId: number) => void
  onFailure: () => void
  onClose?: () => void
}

interface FormValues {
  fileToUpload: File | null
}

export function UploadFileRequestModal({
  parentId,
  params,
  onSuccess,
  onFailure,
  onClose
}: UploadFileRequestModalProps) {
  const context = useServiceContext()
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [fileToUpload, setFileToUpload] = useState<File | null>(params.fileToUpload || null)
  const [canEditBlinded, setCanEditBlinded] = useState(false)
  const [allowBlindingStatusChange, setAllowBlindingStatusChange] = useState(false)
  const [folderType, setFolderType] = useState<any>(null)
  const [isFolderWithinMedidataClinicalLineage, setIsFolderWithinMedidataClinicalLineage] = useState(false)
  const [isUploading, setIsUploading] = useState(false)

  // Modal state for blinding/certified copy
  const [isTriggerChangeBlindingStatus, setIsTriggerChangeBlindingStatus] = useState(false)
  const [isTriggerChangeCertifiedCopy, setIsTriggerChangeCertifiedCopy] = useState(false)
  const { showModal } = useModal()
  const { signAction, showDsoModal, esignMessage, handleDsoSubmit, handleDsoClose } = useEsignature()

  useEffect(() => {
    params?.fileToUpload && params?.isFileExists ? setIsOpen(false) : setIsOpen(true)
    loadData()
  }, [])

  const checkIsFolderWithinMedidataClinicalLineage = (pathArr: any[]) => {
    return pathArr[1]?.name === FolderName.CLINICAL || pathArr[1]?.name === FolderName.MEDIDATA
  }

  const loadData = async () => {
    try {
      const workflow = await context.api.workflows.getWorkflow(params.workflowId)
      // Use documentType?.id if available, fallback to undefined
      // Try both possible documentTypeId sources, fallback to undefined
      const docTypeId = (workflow as any).documentTypeId ?? (workflow as any).documentTypeId ?? undefined
      let docType: any = {}
      if (docTypeId) {
        try {
          docType = await context.api.documentTypes.getById(docTypeId)
        } catch (e) {
          console.error('Error fetching document type:', e)
        }
      }
      const [folderDetails, fsiPath, folderTypeData] = await Promise.all([
        context.api.files.getDetails(workflow.parentFolderId),
        context.api.files.getFsiPath(workflow.parentFolderId),
        context.api.files.getFolderFolderType(workflow.parentFolderId)
      ])
      setAllowBlindingStatusChange((docType as any)?.allowBlindingStatusChange || false)
      setCanEditBlinded(folderDetails.actionFlags?.canEditBlinded || false)
      setIsFolderWithinMedidataClinicalLineage(checkIsFolderWithinMedidataClinicalLineage(fsiPath))
      setFolderType(folderTypeData)
      // If file was provided, auto-submit
      if (params.fileToUpload) {
        submit({ fileToUpload: params.fileToUpload || null })
      }
    } catch (error) {
      context.service.toast.error(t('ERROR_LOADING_DATA'))
    }
  }

  const handleClose = () => {
    // console.log('[UploadFileRequestModal] handleClose called')
    setIsOpen(false)
    onClose?.()
  }

  const validateFile = (): string | null => {
    if (!fileToUpload) {
      return t('ERROR_YOU_MUST_UPLOAD_FILE')
    }

    // Check file extension match for non-placeholder files
    if (params.task?.files && params.task.files.length > 0) {
      const taskFile = params.task.files[0]?.draftFsi

      if (taskFile && taskFile.type !== FsiType.PLACEHOLDER) {
        // Simple extension extraction helper
        const getExtension = (filename: string) => {
          const parts = filename.split('.')
          return parts.length > 1 ? parts[parts.length - 1] : ''
        }

        const taskExt = getExtension(taskFile.name)
        const uploadExt = getExtension(fileToUpload.name)

        if (taskExt && uploadExt && taskExt.toLowerCase() !== uploadExt.toLowerCase()) {
          return t('BROWSER_UPLOAD_FILE_FAIL_FORMAT_MSG', {
            extensionSource: uploadExt,
            extensionTarget: taskExt
          })
        }
      }
    }

    return null
  }

  const submit = async (values: FormValues) => {
    const validationError = validateFile()
    if (validationError) {
      context.service.toast.error(validationError)
      return
    }

    setIsUploading(true)

    // Helper to perform the upload and integrate with uploaderService

    const doUpload = async (blinding: boolean, certified: boolean) => {
      // Move uploadId to outer scope for error handler
      const uploadId = Date.now() + Math.floor(Math.random() * 10000)
      let progressInterval: NodeJS.Timeout | null = null
      try {
        // Close modal before upload
        handleClose()

        // Add upload to uploaderService queue
        const uploadFileObj = fileToUpload!
        const uploadQueueItem = {
          id: uploadId,
          files: [
            {
              name: uploadFileObj.name,
              size: uploadFileObj.size,
              type: uploadFileObj.type,
              progress: () => {
                // uploaderService expects a function returning progress as string
                const found = uploaderService.uploadQueue.find((q) => q.id === uploadId)
                return found && typeof (found as any).progressValue !== 'undefined'
                  ? String((found as any).progressValue)
                  : '0'
              }
            }
          ],
          isCompleted: false,
          isFailed: false,
          isAborted: false,
          progressValue: 0
        }
        uploaderService.uploadQueue.push(uploadQueueItem)
        uploaderService.state.isDisplayed = true
        window.dispatchEvent(new Event('uploader.newUploadStarted'))

        const setProgress = (val: number) => {
          ;(uploadQueueItem as any).progressValue = val
        }
        progressInterval = setInterval(() => {
          if ((uploadQueueItem as any).progressValue < 90) {
            setProgress((uploadQueueItem as any).progressValue + 10)
          }
        }, 200)

        let uploadResult: any
        let eSignatureValidation = null
        try {
          let esignMsg = ''
          if (certified) {
            esignMsg +=
              (context?.service?.auth?.requireESignatureForFileCommands() ? '\n\n' : '') +
              context?.service?.filter?.translate('CERT_COPY_MIGRATION_ESIGN_HEADER') +
              context?.service?.filter?.translate('CERTIFIED_COPY')
            const validation = await signAction('ADMIN_ACTION', esignMsg)
            // Extract only the fields needed by the backend API
            // For SSO/OKTA: backend expects only {relayState}, not the full validation with result
            // For DSO: backend expects only {username, password}
            if (validation?.relayState) {
              // SSO/OKTA authentication - extract only relayState
              eSignatureValidation = { relayState: validation?.relayState }
            } else {
              // DSO authentication - extract only username/password
              eSignatureValidation = {
                username: validation.username || '',
                password: validation.password || ''
              }
            }
          }
        } catch (e) {
          console.error('E-signature validation failed:', e)
          context?.service?.toast?.error(
            context?.service?.filter?.translate('ESIGNATURE_POPUP_SIGNED_WITH_DIFFERENT_ACC')
          )
          return
        }
        try {
          if (params.uploadToVersion && params.task?.files && params.task.files[0]?.draftFsi?.id) {
            // Upload as new version
            uploadResult = await context.api.filesUploadApi.uploadFileVersion(
              uploadFileObj,
              params.task.files[0].draftFsi.id,
              eSignatureValidation,
              blinding,
              certified
            )
          } else {
            // Upload as new file
            uploadResult = await context.api.filesUploadApi.uploadFile(
              uploadFileObj,
              parentId,
              eSignatureValidation,
              blinding,
              certified
            )
          }
          setProgress(100)
          uploadQueueItem.isCompleted = true
          if (progressInterval) clearInterval(progressInterval)
        } catch (error: any) {
          uploadQueueItem.isFailed = true
          if (progressInterval) clearInterval(progressInterval)
          setIsUploading(false)
          onFailure()
          const errorMsg = error?.data?.message || error?.message || t('BROWSER_UPLOAD_FILE_FAIL_MSG')
          context.service.toast.error(errorMsg)
          return
        }

        // Emit events for table updates (match legacy)
        window.dispatchEvent(new CustomEvent('updateFiles', { detail: true }))
        window.dispatchEvent(new CustomEvent('updateTasks', { detail: true }))
        window.dispatchEvent(new CustomEvent('updateDashboard', { detail: true }))

        context.service.toast.success(t('BROWSER_UPLOAD_FILE_SUCCESS_MSG'))
        // Try to extract uploaded file id
        const uploadedId = uploadResult?.id || uploadResult?.fileId || uploadResult?.fsiId || Date.now()
        onSuccess(typeof uploadedId === 'number' ? uploadedId : Date.now())
      } catch (error: any) {
        // Defensive: mark as failed if not already
        const uploadQueueItem = uploaderService.uploadQueue.find((q) => q.id === uploadId)
        if (uploadQueueItem) {
          uploadQueueItem.isFailed = true
        }
        setIsUploading(false)
        onFailure()
        const errorMsg = error?.data?.message || error?.message || t('BROWSER_UPLOAD_FILE_FAIL_MSG')
        context.service.toast.error(errorMsg)
      }
    }

    // Helper to show certified copy modal
    const showCertifiedCopyModal = (blinding: boolean) => {
      showModal(
        <ConfirmationModal
          title={t('MARK_AS_CERTIFIED_COPY_MODAL_TITLE')}
          message={t('MARK_AS_CERTIFIED_COPY_MODAL_TITLE_QUESTION')}
          confirmText={t('YES')}
          cancelText={t('NO')}
          onConfirm={() => doUpload(blinding, true)}
          onCancel={() => doUpload(blinding, false)}
        />,
        { keepParentModalsOpen: true }
      )
    }

    // Modal logic matching legacy
    if (
      folderType?.allowCertifiedCopies &&
      isFolderWithinMedidataClinicalLineage &&
      allowBlindingStatusChange &&
      canEditBlinded
    ) {
      // Show blinding status modal, then certified copy modal
      showModal(
        <ConfirmationModal
          title={t('CONFIRM_BLINDING_STATUS_MODAL_TITLE')}
          message={t('CONFIRM_BLINDING_STATUS_MODAL_TITLE_QUESTION')}
          confirmText={t('YES')}
          cancelText={t('NO')}
          onConfirm={() => {
            setIsTriggerChangeBlindingStatus(true)
            showCertifiedCopyModal(true)
          }}
          onCancel={() => {
            setIsTriggerChangeBlindingStatus(false)
            showCertifiedCopyModal(false)
          }}
        />,
        { keepParentModalsOpen: true }
      )
    } else if (isFolderWithinMedidataClinicalLineage && allowBlindingStatusChange && canEditBlinded) {
      // Show blinding status modal only
      showModal(
        <ConfirmationModal
          title={t('CONFIRM_BLINDING_STATUS_MODAL_TITLE')}
          message={t('CONFIRM_BLINDING_STATUS_MODAL_TITLE_QUESTION')}
          confirmText={t('YES')}
          cancelText={t('NO')}
          onConfirm={() => doUpload(true, false)}
          onCancel={() => doUpload(false, false)}
        />,
        { keepParentModalsOpen: true }
      )
    } else if (folderType?.allowCertifiedCopies) {
      // Show certified copy modal only
      showCertifiedCopyModal(false)
    } else {
      // Direct upload
      doUpload(false, false)
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // console.log('[UploadFileRequestModal] handleFileChange - selected file:', file)
    if (file) {
      setFileToUpload(file)
    }
  }

  return (
    <>
      <Formik
        initialValues={{ fileToUpload }}
        validate={(values) => {
          const errors: any = {}
          if (!values.fileToUpload) {
            errors.fileToUpload = t('ERROR_YOU_MUST_UPLOAD_FILE')
          }
          return errors
        }}
        onSubmit={submit}
        enableReinitialize
        // No innerRef assignment (property does not exist)
      >
        {({ handleSubmit, errors, touched }) => (
          <ModalDialogFn show={isOpen} handleClose={handleClose} title={t('BROWSER_UPLOAD_DOCUMENT_H_TITLE')}>
            <div className="modal-body">
              <ErrorMessageStyled name="fileToUpload" classesName={'fs12 mb20'} />
              <FileUploadModal fileToUpload={fileToUpload} setFileToUpload={setFileToUpload} />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-link" onClick={handleClose} disabled={isUploading}>
                {t('CANCEL')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                onClick={() => handleSubmit()}
                disabled={isUploading || !fileToUpload}
              >
                {isUploading ? t('UPLOADING') : t('BROWSER_UPLOAD_FILE_BTN')}
              </button>
            </div>
          </ModalDialogFn>
        )}
      </Formik>
      {/* E-Signature Modal (for DSO/RCM authentication) */}
      {showDsoModal && (
        <RcmEsignatureModal onSubmit={handleDsoSubmit} onClose={handleDsoClose} esignMessage={esignMessage} />
      )}
    </>
  )
}

export default UploadFileRequestModal
